import React, { useCallback, useEffect, useRef } from "react";
import { FormHelperText, Typography } from "@mui/material";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import { TextField } from "@components/Input/Input.styles";
import { usePortServicesFlow } from "@features/vessel-call/vessel-call-status/PortServices/flow/context/PortServicesFlowContext";
import { internalOnlySchema } from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/approvedByMDGarbageDisposalSchema";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";

const LANG_BASE = "vessel-call.port-services";
const INTERNAL_ATTACHMENT_UPLOAD_KEY = "portServicesInternalAttachment";

const InternalNotesAndAttachmentsComponent = () => {
  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const { t } = useTranslation();
  const { setInternalNotes, internalNotes, errors, setErrors, setInternalAttachment } =
    usePortServicesFlow();

  const charCounterSx = {
    ml: 0,
    mt: 0.5,
    fontSize: 12,
    textAlign: "right",
    color: "text.secondary",
  };

  const isPrintableKey = (e: React.KeyboardEvent) =>
    e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
  const TEXTAREA_MAX_CHARS = 1000;
  const internalUploadSignatureRef = useRef<string | null>(null);
  const setFilesForKey = useUploadStore((s) => s.setFilesForKey);
  const clearFilesForKey = useUploadStore((s) => s.clearFilesForKey);

  const internalFiles = useUploadStore(
    (s) => s.filesByKey[INTERNAL_ATTACHMENT_UPLOAD_KEY] ?? EMPTY_UPLOAD_ENTRIES,
  );

  const fileProfiles = useFileUploadProfiles();
  const internalProfile = fileProfiles.sideDrawerApprovalFile;
  const allowedInternalExtensions = internalProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const setInternalAttachmentFiles = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey(INTERNAL_ATTACHMENT_UPLOAD_KEY);
        return;
      }
      setFilesForKey(INTERNAL_ATTACHMENT_UPLOAD_KEY, files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  const getFileSignature = (entry: UploadEntry | undefined) => {
    if (!(entry instanceof File)) return null;
    return `${entry.name}-${entry.lastModified}-${entry.size}`;
  };

  const triggerUpload = useCallback(
    (
      entry: UploadEntry | undefined,
      handler: ((file: File) => void) | undefined,
      tracker: React.MutableRefObject<string | null>,
    ) => {
      if (!handler) return;
      if (!(entry instanceof File)) return;

      const signature = getFileSignature(entry);
      if (!signature) return;

      if (tracker.current === signature) return;

      tracker.current = signature;
      handler(entry);
    },
    [],
  );

  const removeInternalFile = () => {
    clearFilesForKey(INTERNAL_ATTACHMENT_UPLOAD_KEY);
    setInternalAttachment({ documentUrl: "", documentName: "", fileType: "" });
  };

  const documentUploadMutation = useMutation({
    mutationFn: ({ file, documentType }: { file: File; documentType: string }) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "PortServiceDocumentUpload",
          action: "upload_port_service_document",
          entityType: "port_service_document",
        },
        async (transactionId) => {
          const payloadForSasToken = {
            prefix: SasTokenPrefix.PORT_SERVICES,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };

          const { blobUrl, sasToken } = await getSasToken(
            payloadForSasToken,
            ...(transactionId ? [transactionId] : []),
          );
          const [uploadResult] = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType: documentType,
          });

          const documentUrl = uploadResult?.documentUrl ?? blobUrl;
          setFilesForKey(INTERNAL_ATTACHMENT_UPLOAD_KEY, [
            {
              name: file.name,
              url: documentUrl,
              fileType: file.type,
            },
          ]);
          setInternalAttachment({
            documentUrl: documentUrl,
            documentName: file?.name,
            fileType: file?.type,
          });
          return { documentUrl, documentType };
        },
      ),
  });

  const handleDocumentUpload = (file: File, documentType: string) => {
    documentUploadMutation.mutate({ file, documentType });
  };

  const handleFileUpload = ({
    newFiles,
    setUploadErrors,
    setUploadFiles,
    onUploadDocument,
    uploadTracker,
  }: {
    newFiles: File[];
    setUploadErrors: (
      value:
        | { externalFile?: string; internalFile?: string }
        | ((prev: { externalFile?: string; internalFile?: string }) => {
            externalFile?: string;
            internalFile?: string;
          }),
    ) => void;
    setUploadFiles: (files: UploadEntry[]) => void;
    onUploadDocument?: (file: File) => void;
    uploadTracker: React.MutableRefObject<string | null>;
  }) => {
    if (newFiles.length > 1) {
      setUploadErrors({ externalFile: t("upload.one-document") });
      return;
    }

    const file = newFiles[0];
    if (!file) {
      return;
    }

    setUploadErrors((prev) => ({ ...prev, files: undefined }));
    setUploadFiles([file]);
    triggerUpload(file, onUploadDocument, uploadTracker);
  };

  useEffect(() => {
    return () => {
      internalUploadSignatureRef.current = null;
      removeInternalFile();
    };
  }, []);

  return (
    <div className="flex flex-col gap-2" data-testid="port-services-internal-notes">
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {t(`${LANG_BASE}.fields.internal-notes`)}
      </Typography>
      <TextField
        multiline
        minRows={4}
        value={internalNotes}
        inputProps={{ maxLength: TEXTAREA_MAX_CHARS }}
        placeholder={t(`${LANG_BASE}.fields.internal-notes`)}
        sx={{ "& textarea": { padding: "12px", borderRadius: "8px" } }}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (!isPrintableKey(e)) return;
          if (internalNotes.length >= TEXTAREA_MAX_CHARS) {
            setErrors({
              ...errors,
              internalNotes: t("form-validation.max-length", {
                label: t(`${LANG_BASE}.fields.internal-notes`),
                max: TEXTAREA_MAX_CHARS,
              }),
            });
          }
        }}
        onBlur={() => {
          const res = internalOnlySchema.safeParse({ internalNotes });
          if (!res.success) {
            const flat = res.error.flatten().fieldErrors;
            setErrors({ ...errors, internalNotes: flat.internalNotes?.[0] });
          } else {
            setErrors({ ...errors, internalNotes: undefined });
          }
        }}
        onChange={(e) => {
          const val = e.target.value;
          setInternalNotes(val);
          const res = internalOnlySchema.safeParse({ internalNotes: val });
          if (!res.success) {
            const flat = res.error.flatten().fieldErrors;
            setErrors({ ...errors, internalNotes: flat.internalNotes?.[0] });
          } else {
            setErrors({ ...errors, internalNotes: undefined });
          }
        }}
      />
      {errors.internalNotes && (
        <FormHelperText sx={{ color: "error.main", m: 0 }}>{errors.internalNotes}</FormHelperText>
      )}
      <FormHelperText sx={charCounterSx}>
        {internalNotes.length}/{TEXTAREA_MAX_CHARS}
      </FormHelperText>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {t(`${LANG_BASE}.fields.internal-attachments`)}
      </Typography>
      <DocumentUpload
        testId="port-services-internal-attachment-upload"
        onUpload={(newFiles) =>
          handleFileUpload({
            newFiles,
            setUploadErrors: (fileErrors) =>
              setErrors({
                ...errors,
                ...fileErrors,
              }),
            setUploadFiles: setInternalAttachmentFiles,
            onUploadDocument: (file) => handleDocumentUpload(file, "INTERNAL_NOTES"),
            uploadTracker: internalUploadSignatureRef,
          })
        }
        name={INTERNAL_ATTACHMENT_UPLOAD_KEY}
        conditions={internalProfile}
        files={internalFiles}
        setFiles={(next) => setFilesForKey(INTERNAL_ATTACHMENT_UPLOAD_KEY, next)}
        maxFiles={1}
        allowedExtensions={allowedInternalExtensions}
        uploadDescription={t("upload.dragOrDropDescription")}
      />
      {errors.internalFile && (
        <FormHelperText sx={{ color: "error.main", m: 0 }}>{errors.internalFile}</FormHelperText>
      )}
      {internalFiles[0] && (
        <SingleFile
          file={internalFiles[0]}
          testId="port-services-internal-attachment"
          fieldName={INTERNAL_ATTACHMENT_UPLOAD_KEY}
          currentFileIndex={0}
          setFiles={() => {
            setErrors({ ...errors, internalFile: undefined });
            setInternalAttachmentFiles([]);
            internalUploadSignatureRef.current = null;
          }}
          removeFn={removeInternalFile}
        />
      )}
    </div>
  );
};

export default InternalNotesAndAttachmentsComponent;
